import { FinnhubNewsService } from './FinnhubNewsService';
import { logger } from '../utils/logger';
import { env } from '../config/environment';
import type { NewsArticle } from '../types/market';

export class NewsService {
  private finnhubService: FinnhubNewsService;

  constructor() {
    // Initialize Finnhub service - it will handle missing API keys gracefully
    this.finnhubService = new FinnhubNewsService();
  }

  async getMarketNews(limit: number = 10): Promise<NewsArticle[]> {
    try {
      logger.debug('Fetching market news from Finnhub');
      return await this.finnhubService.getMarketNews(limit);
    } catch (error) {
      logger.error('Error fetching market news', error);
      // Re-throw the error so UI components can handle it appropriately
      throw error;
    }
  }

  async getStockNews(symbol: string, limit: number = 10): Promise<NewsArticle[]> {
    try {
      logger.debug(`Fetching stock news for ${symbol} from Finnhub`);
      return await this.finnhubService.getCompanyNews(symbol, limit);
    } catch (error) {
      logger.error(`Error fetching stock news for ${symbol}`, error);
      // Re-throw the error so UI components can handle it appropriately
      throw error;
    }
  }

  async getTopicNews(topics: string[], limit: number = 10): Promise<NewsArticle[]> {
    try {
      logger.debug(`Fetching general market news from Finnhub for topics: ${topics.join(', ')}`);
      // Since Finnhub doesn't have topic-specific news, return general market news
      return await this.finnhubService.getMarketNews(limit);
    } catch (error) {
      logger.error(`Error fetching topic news for topics: ${topics.join(', ')}`, error);
      // Re-throw the error so UI components can handle it appropriately
      throw error;
    }
  }

  // Get economic calendar events (using general market news)
  async getEconomicNews(limit: number = 10): Promise<NewsArticle[]> {
    try {
      logger.debug('Fetching economic news from Finnhub');
      return await this.finnhubService.getMarketNews(limit);
    } catch (error) {
      logger.error('Error fetching economic news', error);
      // Re-throw the error so UI components can handle it appropriately
      throw error;
    }
  }
}